import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Icon from './Icon';
import { useAuthStore } from '../store/authStore';
import { initials } from '../utils/constants';

interface SidebarNavItem {
  path: string;
  icon: string;
  label: string;
  color: string;
  adminOnly?: boolean;
}

interface SidebarSection {
  title: string;
  items: SidebarNavItem[];
}

const NAV_SECTIONS: SidebarSection[] = [
  {
    title: 'Every day',
    items: [
      { path: '/app/home', icon: 'dashboard', label: 'Dashboard', color: '#c5512d' },
      { path: '/app/chat', icon: 'chat_bubble', label: 'Chat', color: '#80cbc4' },
      { path: '/app/calendar', icon: 'calendar_month', label: 'Calendar', color: '#b39ddb' },
    ],
  },
  {
    title: 'Home',
    items: [
      { path: '/app/lists', icon: 'shopping_cart', label: 'Shopping', color: '#e9c46a' },
      { path: '/app/kitchen', icon: 'restaurant', label: 'Kitchen', color: '#4fc3f7' },
      { path: '/app/pantry', icon: 'kitchen', label: 'Pantry', color: '#8ab07e' },
      { path: '/app/tasks', icon: 'check_circle', label: 'Tasks', color: '#81c784' },
      { path: '/app/reminders', icon: 'notifications', label: 'Reminders', color: '#ffb74d' },
    ],
  },
  {
    title: 'Family',
    items: [
      { path: '/app/school-calendar', icon: 'school', label: 'School', color: '#b39ddb' },
      { path: '/app/health', icon: 'local_hospital', label: 'Health', color: '#ef5350' },
      { path: '/app/contacts', icon: 'contacts', label: 'Contacts', color: '#f48fb1' },
      { path: '/app/special-days', icon: 'star', label: 'Special Days', color: '#ff8a65' },
      { path: '/app/locator', icon: 'location_on', label: 'Map', color: '#80deea' },
    ],
  },
  {
    title: 'Memories',
    items: [
      { path: '/app/notes', icon: 'sticky_note_2', label: 'Notes', color: '#ffb74d' },
      { path: '/app/wishlist', icon: 'favorite', label: 'Wishlist', color: '#f48fb1' },
      { path: '/app/polls', icon: 'poll', label: 'Polls', color: '#b39ddb' },
    ],
  },
  {
    title: 'Admin',
    items: [
      { path: '/app/budget', icon: 'account_balance_wallet', label: 'Budget', color: '#4fc3f7', adminOnly: true },
      { path: '/app/documents', icon: 'folder_open', label: 'Docs', color: '#64b5f6' },
    ],
  },
];

const Sidebar: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const { user, family, logout } = useAuthStore();
  const isAdmin = family?.role === 'admin';
  const isActive = (path: string) => location.pathname === path || (path !== '/app/home' && location.pathname.startsWith(path));

  const handleLogout = () => {
    logout();
    history.push('/login');
  };

  return (
    <aside className="fh-sidebar" aria-label="Main navigation">
      {/* Profile section */}
      <button className="fh-sidebar-profile" onClick={() => history.push('/app/profile')}>
        <div
          className="fh-sidebar-avatar"
          style={{
            background: user?.colour || 'var(--fh-primary)',
            boxShadow: `0 0 0 2px var(--fh-dark-bg), 0 0 0 4px ${user?.colour || '#a43917'}40`,
          }}
        >
          {user?.avatar ? (
            <img src={user.avatar} alt=""  loading="lazy"/>
          ) : (
            <span>{user ? initials(user.displayName || user.email) : '?'}</span>
          )}
        </div>
        <div className="fh-sidebar-profile-info">
          <h2 className="fh-sidebar-family-name">{family?.name || 'FamHearth'}</h2>
          <p className="fh-sidebar-tagline">
            {family?.members?.length
              ? `House · ${family.members.length} member${family.members.length === 1 ? '' : 's'}`
              : 'Life. Organised.'}
          </p>
        </div>
      </button>

      {/* Navigation */}
      <nav className="fh-sidebar-nav">
        {NAV_SECTIONS.map(section => {
          const visibleItems = section.items.filter(item => !item.adminOnly || isAdmin);
          if (visibleItems.length === 0) return null;
          return (
            <div key={section.title} className="fh-sidebar-section">
              <div className="fh-sidebar-section-label">{section.title}</div>
              {visibleItems.map(item => {
                const active = isActive(item.path);
                return (
                  <button
                    key={item.path}
                    onClick={() => history.push(item.path)}
                    className={`fh-sidebar-nav-item ${active ? 'fh-sidebar-nav-active' : ''}`}
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon name={item.icon} size={20} color={active ? item.color : `${item.color}90`} weight={active ? 'fill' : 'duotone'} />
                    <span>{item.label}</span>
                  </button>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* Footer */}
      <div className="fh-sidebar-footer">
        <button
          onClick={() => history.push('/app/more')}
          className="fh-sidebar-nav-item"
        >
          <Icon name="settings" size={20} color="var(--fh-dark-text-muted)" weight="duotone" />
          <span>Settings</span>
        </button>
        <button
          onClick={handleLogout}
          className="fh-sidebar-nav-item"
        >
          <Icon name="logout" size={20} color="var(--fh-dark-text-muted)" weight="duotone" />
          <span>Log out</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
